// src/pages/CompanyPages/MainProfile/EditProfile/EditContactProfile.tsx
import React from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import { Input } from '../../../../UI/Input/Input';
import { EditWrapper } from '../../../../components/EditWrapper/EditWrapper';
import { axiosInstance } from '../../../../helpers/axios';
import { useAppDispatch } from '../../../../hooks/reduxHooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/reduxHooks/useAppSelector';
import { PATH_DASHBOARD } from '../../../../routes/RouterConfig';
import { getCompanyRetrieve } from '../../../../store/reducers/CompanySlice';

import { getOrganizationContactEditSchema } from '../../../../validations/organizationValidation';
import styles from './EditProfile.module.scss';

interface ContactFormValues {
    contact_email: string;
    contact_phone: string;
    first_name: string;
    second_name: string;
    last_name: string;
    contact_person_position: string;
}

export const EditContactProfile: React.FC = () => {
    const id = useAppSelector((state) => state.authReducer.id);
    const company = useAppSelector((state) => state.companyReducer.company);

    const dispatch = useAppDispatch();

    const initialValues: ContactFormValues = {
        contact_email: company?.contact_email ?? '',
        contact_phone: company?.contact_phone ?? '',
        first_name: company?.first_name ?? '',
        second_name: company?.second_name ?? '',
        last_name: company?.last_name ?? '',
        contact_person_position: company?.contact_person_position ?? '',
    };

    const formik = useFormik<ContactFormValues>({
        validateOnMount: true,
        enableReinitialize: true,
        initialValues,
        validationSchema: getOrganizationContactEditSchema(),
        onSubmit: async (values, { setSubmitting }) => {
            if (!id) {
                toast.error('Не удалось определить пользователя');
                return;
            }

            const url = `/v1/company/${id}/`;
            try {
                await axiosInstance.put(url, values);
                toast.success('Данные профиля успешно изменены');
                dispatch(getCompanyRetrieve(Number(id)));
            } catch (e) {
                toast.error('Произошла ошибка. Попробуйте позже');
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div className={styles.companyEditProfile}>
            <form onSubmit={formik.handleSubmit} className={styles.EditProfile}>
                <EditWrapper
                    saveLink={PATH_DASHBOARD.company.root}
                    rejectLink={PATH_DASHBOARD.company.root}
                    title={'Контактное лицо'}
                    isValid={formik.isValid}
                    dirty={formik.dirty}
                    saveOnclick={formik.handleSubmit}
                >
                    <div className={styles.EditAbout__wrapper}>
                        <Input
                            wrapperClassName={[styles.EditAbout__input]}
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Фамилия'}
                            placeholder={'Фамилия'}
                            name="second_name"
                            value={formik.values.second_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.second_name ? formik.errors.second_name : ''}
                            isValid={Boolean(formik.touched.second_name && !formik.errors.second_name)}
                        />

                        <Input
                            wrapperClassName={[styles.EditAbout__input]}
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Имя'}
                            placeholder={'Имя'}
                            name="first_name"
                            value={formik.values.first_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.first_name ? formik.errors.first_name : ''}
                            isValid={Boolean(formik.touched.first_name && !formik.errors.first_name)}
                        />

                        <Input
                            wrapperClassName={[styles.EditAbout__input]}
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Отчество'}
                            placeholder={'Отчество'}
                            name="last_name"
                            value={formik.values.last_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.last_name ? formik.errors.last_name : ''}
                            isValid={Boolean(formik.touched.last_name && !formik.errors.last_name)}
                        />

                        <Input
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Email'}
                            placeholder={'Email'}
                            name="contact_email"
                            value={formik.values.contact_email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.contact_email ? formik.errors.contact_email : ''}
                            isValid={Boolean(formik.touched.contact_email && !formik.errors.contact_email)}
                        />

                        <Input
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Телефон'}
                            placeholder={'+78008008080'}
                            name="contact_phone"
                            value={formik.values.contact_phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.contact_phone ? formik.errors.contact_phone : ''}
                            isValid={Boolean(formik.touched.contact_phone && !formik.errors.contact_phone)}
                        />

                        <Input
                            wrapperClassName={[styles.EditAbout__input]}
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            inputHeader={'Должность'}
                            placeholder={'Должность'}
                            name="contact_person_position"
                            value={formik.values.contact_person_position}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            errorMessage={formik.touched.contact_person_position ? formik.errors.contact_person_position : ''}
                            isValid={Boolean(formik.touched.contact_person_position && !formik.errors.contact_person_position)}
                        />
                    </div>
                </EditWrapper>
            </form>
        </div>
    );
};

export default EditContactProfile;
